import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Store, Search, Ban, CheckCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { PlanRequestsPanel } from "@/components/admin/PlanRequestsPanel";

const AdminSellers = () => {
  const { getAuthHeaders } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSellers();
  }, [filter]);

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      const res = await axios.get(`${API}/admin/sellers?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setSellers(res.data.sellers || []);
    } catch (error) {
      toast.error("Failed to load sellers");
    }
    setLoading(false);
  };

  const approveSeller = async (sellerId) => {
    try {
      await axios.put(`${API}/admin/sellers/${sellerId}/approve`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Seller approved");
      fetchSellers();
    } catch (error) {
      toast.error("Failed to approve seller");
    }
  };

  const toggleBlock = async (sellerId) => {
    try {
      await axios.put(`${API}/admin/sellers/${sellerId}/block`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Seller status updated");
      fetchSellers();
    } catch (error) {
      toast.error("Failed to update seller");
    }
  };

  const changePlan = async (sellerId, planId) => {
    try {
      await axios.put(`${API}/admin/sellers/${sellerId}/plan`, { plan_id: planId }, { headers: getAuthHeaders() });
      toast.success("Seller plan updated");
      fetchSellers();
    } catch (error) {
      toast.error(apiError(error, "Failed to update plan"));
    }
  };

  const filteredSellers = sellers.filter(
    (s) =>
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.business_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.mobile?.includes(search)
  );

  return (
    <AdminLayout title="Sellers">
      <PlanRequestsPanel onChange={fetchSellers} />
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, business, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-sellers-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-sellers">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sellers</SelectItem>
            <SelectItem value="pending">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sellers Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
            </div>
          ) : filteredSellers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Seller</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSellers.map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{seller.name}</p>
                          <p className="text-sm text-gray-500">{seller.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{seller.business_name}</p>
                          <p className="text-xs text-gray-500">{seller.business_type}</p>
                        </div>
                      </TableCell>
                      <TableCell>{seller.mobile}</TableCell>
                      <TableCell>
                        <a
                          href={`/store/${seller.store_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline flex items-center gap-1"
                        >
                          {seller.store_slug}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </TableCell>
                      <TableCell>
                        <Select value={seller.plan || "starter"} onValueChange={(v) => changePlan(seller.id, v)}>
                          <SelectTrigger className="w-32" data-testid={`seller-plan-select-${seller.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="growth">Growth</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {seller.is_approved ? (
                            <Badge className="bg-green-100 text-green-700">Approved</Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                          )}
                          {seller.is_blocked && (
                            <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {!seller.is_approved && (
                            <Button
                              size="sm"
                              onClick={() => approveSeller(seller.id)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                              data-testid={`approve-seller-${seller.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleBlock(seller.id)}
                            className={seller.is_blocked ? "text-green-600" : "text-red-600"}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No sellers found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminSellers;
